'use client'

import { useState, useEffect, useMemo } from 'react'
import Pill from '@/components/ui/Pill'
import RichText from '@/components/content/common/RichText'
import DealClaimButton from '@/components/content/deals/DealClaimButton'
import { DealOverview, DealEligibility } from '@/components/content/deals/deal/DealSummary'
import ComparisonThumbnail from '@/components/content/comparisons/root/ComparisonThumbnail'
import ReviewThumbnail from '@/components/content/reviews/root/ReviewThumbnail'
import { FeaturedTagType } from '@/collections/FeaturedTag/constants'
import { createLogger } from '@/lib/logger'
import { queryPayload } from '@/lib/payloadClient'
import { determineDealsToDisplay } from '@/components/content/deals/helpers'
import { isPopulatedObject } from '@/lib/utils'
import { sortBlogComparisons, sortDealsByAppOrder } from '@/lib/blog-app-order'
import type { SectionItem, Block } from '@/types/app'
import type { App, Deal, Comparison, ComparisonAppsApp } from '@/payload-types'

const logger = createLogger('Tabs')

type TabContent = {
  items: SectionItem[]
  totalCount: number
  comparisons?: Comparison[]
}

export function useDealsTab(
  apps: App | App[],
  { sortByApp = false }: { sortByApp?: boolean } = {},
): TabContent {
  const appsArray = Array.isArray(apps) ? apps : [apps]

  const activeDeals = determineDealsToDisplay(
    appsArray.flatMap((app) => {
      const docs = (app.deals?.docs as Deal[] | undefined) ?? []
      return docs.map((deal) => ({ ...deal, appId: app.id }))
    }),
  )
  const deals = sortByApp ? sortDealsByAppOrder(activeDeals, appsArray) : activeDeals

  // Deals
  const dealsSection: SectionItem[] =
    deals?.map((deal) => {
      const app = appsArray.find((app) => app.id === deal.appId) as App
      const link = app?.slug ? `/deals/${app.slug}` : '/deals'

      return {
        title: deal.name ?? 'Untitled',
        content: (
          <div className="flex w-full flex-col gap-4 rounded-xl border border-gray-200 p-4 dark:border-white/10">
            <ReviewThumbnail reviewApp={app} hideButton={true} customLink={link} />
            <div className="flex flex-col gap-3 rounded-xl border border-gray-200 bg-gray-50 p-4 dark:border-white/10 dark:bg-white/10 xs:flex-row xs:items-center">
              <div className="flex flex-col lg:flex-row lg:items-center lg:gap-3">
                <DealOverview deal={deal} invert={true} />
                <DealEligibility
                  eligibility={deal.eligibility}
                  terms={deal.terms}
                  invert={true}
                  hideInfo={true}
                />
              </div>
              <DealClaimButton deal={deal} app={app} className="xs:ml-auto">
                <Pill style="outlinePrimary">Claim</Pill>
              </DealClaimButton>
            </div>
          </div>
        ),
      }
    }) ?? []

  return {
    items: dealsSection,
    totalCount: deals.length,
  }
}

/*
 * overlappingAppIds is currently used only on the alternatives page
 * If using a singular app, wrap it in an array: [app]
 */
export function useComparisonsTab(
  apps: App[],
  page: 'alternative' | 'stack' | 'blog',
  overlappingAppIds?: Array<App['id']>,
): TabContent {
  const [comparisons, setComparisons] = useState<Comparison[]>([])
  const [loading, setLoading] = useState(true)

  const appIds = useMemo(() => {
    return apps
      .map((app) => app.id)
      .sort()
      .join(',')
  }, [apps])

  useEffect(() => {
    if (!Array.isArray(apps)) {
      logger.error('Apps must be an array of App objects')
    }
  }, [apps])

  useEffect(() => {
    if (['alternative', 'stack'].includes(page) && !overlappingAppIds) {
      logger.error(`overlappingAppIds is required for ${page} page`)
    }

    if (!['alternative', 'stack'].includes(page) && overlappingAppIds) {
      logger.error(
        'overlappingAppIds is not currently used for non-alternative and non-stack pages',
      )
    }
  }, [page, overlappingAppIds])

  useEffect(() => {
    let isMounted = true

    async function fetchAndSetComparisons() {
      try {
        if (isMounted) {
          setLoading(true)
        }

        const { docs: comparisonAppDocs } = await queryPayload({
          collection: 'comparison_apps_app',
          select: {
            comparison: true,
          },
          populate: {
            comparison: {
              name: true,
              comparisonApps: true,
              tldr: true,
            },
            comparison_apps_app: {
              app: true,
            },
            app: {
              icon: true,
              glyph: true,
              slug: true,
              name: true,
              partnershipTier: true,
              brandBackgroundColor: true,
              brandPrimaryColor: true,
              brandSecondaryColor: true,
              brandTertiaryColor: true,
              categoryRecommendations: true,
            },
            // we are now fetching much less data with the category specified
            category: {
              name: true,
            },
            app_category_recommendation: {
              classification: true,
              discontinuedAt: true,
              recommendation: true,
              bestFor: true,
              summary: true,
              order: true,
              category: true,
            },
            media: {
              url: true,
              alt: true,
              filename: true,
              coveragePercent: true,
              xOffsetPercent: true,
              yOffsetPercent: true,
            },
          },
          where: {
            app: { in: apps.map((app) => app.id) },
            'comparison._status': { equals: 'published' },
          },
          depth: 5,
          sort: ['order'],
          pagination: false,
          cacheKey: `query:caa:apps:${page}:${appIds}`,
        })

        if (isMounted) {
          const comparisonApps = comparisonAppDocs.map((doc) => doc.comparison as Comparison)

          const uniqueComparisons = comparisonApps.reduce(
            (acc, comparison) => {
              if (!acc.seen.has(comparison.id)) {
                acc.comparisons.push(comparison)
                acc.seen.add(comparison.id)
              }
              return acc
            },
            { comparisons: [] as Comparison[], seen: new Set<Comparison['id']>() },
          )

          // If multiple apps, filter to only comparisons between the passed apps
          let filteredComparisons = uniqueComparisons.comparisons
          if (apps.length > 1) {
            const passedAppIds = new Set(apps.map((app) => app.id))

            filteredComparisons = uniqueComparisons.comparisons.filter((comparison) => {
              // Get all app IDs in this comparison
              const comparisonAppIds = (comparison.comparisonApps?.docs ?? [])
                .map((doc) => {
                  if (!isPopulatedObject<ComparisonAppsApp>(doc)) return undefined
                  const app = doc.app
                  if (!isPopulatedObject<App>(app)) return undefined
                  return app.id
                })
                .filter((id): id is number => typeof id === 'number')

              // Count how many apps in this comparison are from our passed apps
              const matchCount = comparisonAppIds?.filter((id) => passedAppIds.has(id)).length ?? 0

              // Show comparison if at least 2 of its apps are in our passed array
              return matchCount >= 2
            })
          } else if (overlappingAppIds) {
            // if a single app was passed in AND overlappingApps is provided, filter to only comparisons that include the overlapping apps
            const uniqueOverlappingAppIds = new Set(overlappingAppIds)

            filteredComparisons = uniqueComparisons.comparisons.filter((comparison) => {
              // Get all app IDs in this comparison
              const comparisonAppIds = (comparison.comparisonApps?.docs ?? [])
                .map((doc) => {
                  if (!isPopulatedObject<ComparisonAppsApp>(doc)) return undefined
                  const app = doc.app
                  if (!isPopulatedObject<App>(app)) return undefined
                  return app.id
                })
                .filter((id): id is number => typeof id === 'number')

              // Count how many apps in this comparison are from our overlapping apps
              const matchCount =
                comparisonAppIds?.filter((id) => uniqueOverlappingAppIds.has(id)).length ?? 0

              // Show comparison if at least 1 of its apps is in our overlapping apps
              return matchCount >= 1
            })
          }

          setComparisons(filteredComparisons)
        }
      } catch (error) {
        if (isMounted) {
          logger.error('Error fetching comparisons:', error)
        }
      } finally {
        if (isMounted) {
          setLoading(false)
        }
      }
    }

    fetchAndSetComparisons()

    return () => {
      isMounted = false
    }
  }, [appIds])

  const orderedComparisons = useMemo(
    () => (page === 'blog' ? sortBlogComparisons(comparisons, apps) : comparisons),
    [comparisons, apps, page],
  )

  // Comparisons
  const comparisonsSection: SectionItem[] =
    orderedComparisons.length > 0
      ? orderedComparisons.map((comparison) => {
          return {
            title: comparison.name ?? 'Untitled',
            content: (
              <div className="flex flex-col gap-4">
                <RichText content={(comparison.tldr?.root?.children as Block[]) ?? []} />
                <ComparisonThumbnail
                  comparison={comparison}
                  type={FeaturedTagType.ThumbnailPrimary}
                />
              </div>
            ),
          }
        })
      : []

  return {
    items: comparisonsSection,
    totalCount: comparisons.length,
    comparisons: orderedComparisons,
  }
}
